import React, { useState, useRef } from 'react';
import { socialWallApi } from '../../services/api';
import { X, Image, XCircle } from 'react-feather';
import { Button } from '../ui/button';
import './SocialWall.css';

const PostForm = ({ onClose, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('regular');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') || file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('Please select image or video files only');
      return;
    }

    const newFiles = [...selectedFiles, ...imageFiles];
    setSelectedFiles(newFiles);

    // Create preview URLs
    const newPreviews = imageFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }));
    setPreviewUrls([...previewUrls, ...newPreviews]);
  };

  const handleRemoveFile = (index) => {
    const fileToRemove = previewUrls[index];
    URL.revokeObjectURL(fileToRemove.url);
    
    const newFiles = selectedFiles.filter((_, i) => {
      const fileIndex = previewUrls.findIndex(p => p.file === selectedFiles[i]);
      return fileIndex !== index;
    });
    
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() && selectedFiles.length === 0) {
      alert('Please add some content or select files');
      return;
    }

    setIsSubmitting(true);
    try {
      const postData = {
        content: content.trim() || 'Shared a photo',
        post_type: postType
      };
      
      const newPost = await socialWallApi.create(postData, selectedFiles);
      onPostCreated(newPost);
      
      // Cleanup preview URLs
      previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
      
      // Reset form
      setContent('');
      setPostType('regular');
      setSelectedFiles([]);
      setPreviewUrls([]);
    } catch (err) {
      console.error('Error creating post:', err);
      alert('Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Cleanup preview URLs
    previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
    onClose();
  };

  return (
    <div className="post-form-overlay">
      <div className="post-form-modal">
        <div className="post-form-header">
          <h2>Create Post</h2>
          <button className="post-form-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="post-form">
          <div className="post-form-type">
            <label>Post Type:</label>
            <select
              value={postType}
              onChange={(e) => setPostType(e.target.value)}
              className="post-type-select"
            >
              <option value="regular">Regular</option>
              <option value="birthday">Birthday</option>
              <option value="celebration">Celebration</option>
              <option value="announcement">Announcement</option>
            </select>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            className="post-form-textarea"
            rows={6}
          />

          {previewUrls.length > 0 && (
            <div className="post-form-previews">
              {previewUrls.map((preview, index) => (
                <div key={index} className="post-form-preview">
                  {preview.type === 'image' ? (
                    <img src={preview.url} alt={`Preview ${index + 1}`} />
                  ) : (
                    <video src={preview.url} controls />
                  )}
                  <button
                    type="button"
                    className="post-form-remove-preview"
                    onClick={() => handleRemoveFile(index)}
                  >
                    <XCircle size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="post-form-actions">
            <button
              type="button"
              className="post-form-upload-button"
              onClick={() => fileInputRef.current?.click()}
            >
              <Image size={18} />
              Add Photos/Videos
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <div className="post-form-submit-actions">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || (!content.trim() && selectedFiles.length === 0)}
              >
                {isSubmitting ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostForm;

